import ApplyV2InputText from '@core/components/ApplyDialogV2/ApplyV2InputText'
import CountryIcon from '@core/components/ApplyDialogV2/ApplyV2ProfileStep/PhoneInput/CountryIcon'
import WarningErrorIcon from '@core/components/icons/WarningErrorIcon'
import decodeHtmlEntitiesRecursive from '@core/utils/helpers/decodeHtmlEntitiesRecursive'
import useApplyV2NavTranslations from '@core/utils/hooks/useApplyV2NavTranslations'
import clsx from 'clsx'
import React, { useCallback, useEffect, useId, useRef, useState } from 'react'

import { countryPhones, type PrefixData } from './constants'

import './ApplyV2InputPhone.scss'

interface PhoneValue {
    prefix: string | undefined | null
    number: string | undefined | null
}

interface ApplyV2InputPhoneProps {
    id?: string | number
    name: string
    label: string
    value?: PhoneValue | null
    placeholder?: string
    onChange: (value: PhoneValue) => void
    onFocus?: React.FocusEventHandler<HTMLDivElement>
    onBlur?: React.FocusEventHandler<HTMLDivElement>
    errorMessage?: string | { prefix?: string; number?: string } | false | null | undefined
    isRequired?: boolean
    className?: string
    isDisabled?: boolean
    isLoading?: boolean
    isShowErrorMessage?: boolean
    description?: string | number | null
}

const baseClass = 'ApplyV2InputPhone'
function ApplyV2InputPhone({
    id,
    name,
    label,
    value = { prefix: '', number: '' },
    placeholder = '+1 234 567 89 00',
    onChange,
    onBlur,
    onFocus,
    errorMessage,
    isRequired = false,
    className,
    isDisabled = false,
    isLoading = false,
    isShowErrorMessage = true,
    description,
}: ApplyV2InputPhoneProps) {
    const defaultId = useId()
    const selectRef = useRef<any>(null)
    const inputRef = useRef<any>(null)
    const wrapperRef = useRef<HTMLDivElement>(null)
    const menuRef = useRef<HTMLDivElement>(null)
    const searchInputRef = useRef<HTMLInputElement>(null)
    const [menuWidth, setMenuWidth] = useState('100%')
    const { getTitle } = useApplyV2NavTranslations()
    const [searchTerm, setSearchTerm] = useState('')
    const [isMenuOpen, setIsMenuOpen] = useState(false)
    const [menuPosition, setMenuPosition] = useState<'bottom' | 'top'>('bottom')
    const [isInputFocused, setIsInputFocused] = useState(false)
    const [focusedIndex, setFocusedIndex] = useState(-1)
    const itemRefs = useRef<(HTMLDivElement | null)[]>([])
    const [templateFlag, setTemplateFlag] = useState<number | null | undefined>(null)

    const groupId = id || defaultId

    const countryFlag =
        countryPhones.find((c) => c.id === templateFlag)?.flag ??
        countryPhones.find(
            (c) => c.prefix === value?.prefix || c.prefix === `+${value?.prefix}` || `+${c.prefix}` === value?.prefix
        )?.flag

    const filteredCountries = React.useMemo(() => {
        return (
            countryPhones
                .filter((item) => {
                    return (
                        item.name.toLowerCase().startsWith(searchTerm.toLowerCase().trim()) ||
                        // item.name.toLowerCase().includes(searchTerm.toLowerCase().trim()) ||
                        item.prefix.toLowerCase().includes(searchTerm.toLowerCase().trim())
                    )
                })
                // .sort((a, b) => {
                //     return a.prefix > b.prefix ? 1 : -1
                // })
                .sort((a, b) => {
                    return a.name > b.name ? 1 : -1
                })
        )
    }, [searchTerm])

    const getDisplayValue = () => {
        return `${value?.prefix || ''}${value?.number || ''}`
    }

    const calculateMenuPosition = useCallback(() => {
        if (!wrapperRef.current) return 'bottom'

        const rect = wrapperRef.current.getBoundingClientRect()
        const spaceBelow = window.innerHeight - rect.bottom
        const spaceAbove = rect.top

        if (spaceBelow < 300 && spaceAbove > 300) {
            return 'top'
        }
        return 'bottom'
    }, [])

    const handlePrefixSelect = (selectedOption: PrefixData) => {
        setIsMenuOpen(false)
        setTemplateFlag(selectedOption.id)
        setSearchTerm(selectedOption?.prefix ?? '')
        onChange({
            number: value?.number,
            prefix: selectedOption?.prefix,
        })
        console.log('selectedOption', selectedOption)
        setTimeout(() => {
            inputRef.current?.focus()
        }, 0)
    }

    const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let inputValue = e.target.value

        inputValue = inputValue.replace(/[^\d+]/g, '')

        if (inputValue.includes('+') && !inputValue.startsWith('+')) {
            inputValue = inputValue.replace(/\+/g, '')
            inputValue = '+' + inputValue
        }

        const plusCount = (inputValue.match(/\+/g) || []).length
        if (plusCount > 1) {
            inputValue = inputValue.replace(/\+/g, '')
            inputValue = '+' + inputValue
        }

        if (inputRef.current && inputRef.current.value !== inputValue) {
            inputRef.current.value = inputValue
        }

        if (inputValue === '') {
            onChange({ prefix: '', number: '' })
            return
        }

        let foundPrefix = countryPhones.find((item) => inputValue.startsWith(item.prefix))?.prefix

        if (!foundPrefix && !inputValue.startsWith('+')) {
            const numericValue = inputValue.replace('+', '')

            const possiblePrefixes = countryPhones
                .filter((item) => numericValue.startsWith(item.prefix.replace('+', '')))
                .sort((a, b) => b.prefix.length - a.prefix.length)

            if (possiblePrefixes.length > 0) {
                foundPrefix = possiblePrefixes[0].prefix
            }
        }

        let newPrefix = value?.prefix || ''
        let newNumber = inputValue

        if (foundPrefix) {
            newPrefix = foundPrefix
            newNumber = inputValue.substring(foundPrefix.length).trim()
        } else {
            if (value?.prefix && !inputValue.startsWith(value.prefix)) {
                newPrefix = ''
                newNumber = inputValue
            }
        }

        onChange({
            prefix: newPrefix,
            number: newNumber,
        })
    }

    const closeMenu = useCallback(() => {
        setIsMenuOpen(false)
        // setSearchTerm('')
    }, [])

    const toggleMenu = () => {
        if (isMenuOpen) {
            closeMenu()
        } else {
            const position = calculateMenuPosition()
            setMenuPosition(position)
            setIsMenuOpen(true)
            setSearchTerm(value?.prefix ?? '')
        }
    }

    const updateMenuPosition = useCallback(() => {
        if (isMenuOpen) {
            const position = calculateMenuPosition()
            setMenuPosition(position)
        }
    }, [isMenuOpen, calculateMenuPosition])

    useEffect(() => {
        updateMenuPosition()
    }, [isMenuOpen, updateMenuPosition])

    useEffect(() => {
        window.addEventListener('resize', updateMenuPosition)
        // window.addEventListener('scroll', updateMenuPosition)

        return () => {
            window.removeEventListener('resize', updateMenuPosition)
            // window.removeEventListener('scroll', updateMenuPosition)
        }
    }, [updateMenuPosition])

    useEffect(() => {
        const handleScroll = () => {
            updateMenuPosition()
        }

        const findScrollableContainers = () => {
            const containers: Element[] = []
            let currentElement = wrapperRef.current?.parentElement

            while (currentElement) {
                const style = window.getComputedStyle(currentElement)
                if (style.overflowY === 'auto' || style.overflowY === 'scroll') {
                    containers.push(currentElement)
                }
                currentElement = currentElement.parentElement
            }

            return containers
        }

        if (isMenuOpen) {
            const scrollableContainers = findScrollableContainers()

            scrollableContainers.forEach((container) => {
                container.addEventListener('scroll', handleScroll)
            })

            return () => {
                scrollableContainers.forEach((container) => {
                    container.removeEventListener('scroll', handleScroll)
                })
            }
        }
    }, [isMenuOpen, updateMenuPosition])

    useEffect(() => {
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && isMenuOpen) {
                closeMenu()
            }
        }

        document.addEventListener('keydown', handleEscape)
        return () => document.removeEventListener('keydown', handleEscape)
    }, [isMenuOpen, closeMenu])

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                isMenuOpen &&
                // wrapperRef.current &&
                // !wrapperRef.current.contains(event.target as Node) &&
                selectRef.current &&
                !selectRef.current.contains(event.target as Node)
            ) {
                closeMenu()
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [isMenuOpen, closeMenu])

    useEffect(() => {
        if (wrapperRef.current) {
            setMenuWidth(`${wrapperRef.current.clientWidth}px`)
        }
    }, [])

    useEffect(() => {
        if (isMenuOpen && searchInputRef.current) {
            setTimeout(() => {
                searchInputRef.current?.focus()
            }, 100)
        }
    }, [isMenuOpen])

    const getErrorMessage = (field: 'prefix' | 'number'): string | undefined => {
        if (!errorMessage || typeof errorMessage === 'boolean') return undefined
        if (typeof errorMessage === 'string') return errorMessage
        return errorMessage[field]
    }

    useEffect(() => {
        if (!isMenuOpen) return

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'ArrowDown') {
                event.preventDefault()
                setFocusedIndex((prev) => {
                    const next = prev + 1
                    return next >= filteredCountries.length ? 0 : next
                })
            }
            if (event.key === 'ArrowUp') {
                event.preventDefault()
                setFocusedIndex((prev) => {
                    const next = prev - 1
                    return next < 0 ? filteredCountries.length - 1 : next
                })
            }
            if (event.key === 'Enter' && focusedIndex >= 0) {
                event.preventDefault()
                handlePrefixSelect(filteredCountries[focusedIndex])
            }
        }

        document.addEventListener('keydown', handleKeyDown)
        return () => document.removeEventListener('keydown', handleKeyDown)
    }, [isMenuOpen, filteredCountries, focusedIndex, handlePrefixSelect])

    useEffect(() => {
        if (focusedIndex >= 0 && itemRefs.current[focusedIndex]) {
            itemRefs.current[focusedIndex]?.scrollIntoView({
                block: 'nearest',
                // block: 'center',
                // behavior: 'smooth',
            })
        }
    }, [focusedIndex])

    useEffect(() => {
        if (isMenuOpen && value?.prefix) {
            const index = filteredCountries.findIndex((c) => c.prefix === value.prefix)
            if (index >= 0) {
                setFocusedIndex(index)
            }
        }
        if (!isMenuOpen) {
            setFocusedIndex(-1)
        }
    }, [isMenuOpen, value, filteredCountries])

    const errors = [...new Set([getErrorMessage('prefix'), getErrorMessage('number')])].filter(Boolean).join(', ')

    return (
        <div
            className={clsx(baseClass, className, {
                [`${baseClass}--${name}`]: name,
                [`${baseClass}--disabled`]: isDisabled,
                [`${baseClass}--loading`]: isLoading,
                [`${baseClass}--error`]: !!getErrorMessage('prefix') && !!getErrorMessage('number'),
                [`${className}--disabled`]: className && isDisabled,
                [`${className}--loading`]: className && isLoading,
                [`${className}--error`]: className && !!getErrorMessage('prefix') && !!getErrorMessage('number'),
            })}
            onFocus={onFocus}
            tabIndex={-1}
        >
            {label && (
                <div
                    className={clsx(`${baseClass}__header`, {
                        [`${className}__header`]: !!className,
                    })}
                >
                    <div
                        className={clsx(`${baseClass}__header--title`, {
                            [`${className}__header--title`]: !!className,
                        })}
                    >
                        {decodeHtmlEntitiesRecursive(label)}
                        {isRequired && '*'}
                    </div>
                </div>
            )}
            <div
                ref={wrapperRef}
                className={clsx(`${baseClass}__fields`, {
                    [`${baseClass}__fields__focused`]: isInputFocused,
                    [`${baseClass}__fields--error`]: !!errors,
                })}
            >
                <div
                    // type="button"
                    ref={selectRef}
                    className={clsx(`${baseClass}__fields__select`, {
                        [`${baseClass}__fields__select__focused`]: isInputFocused,
                        [`${baseClass}__fields__select--error`]: !!errors,
                    })}
                    onFocus={onFocus}
                    tabIndex={-1}
                    // tabIndex={0}
                    onClick={toggleMenu}
                >
                    <div className={`${baseClass}__fields__select--value`}>
                        <CountryIcon flag={countryFlag} />
                        <div className={`${baseClass}__fields__select--value--chevron`}>
                            {isMenuOpen ? (
                                <i className="fa-solid fa-chevron-up"></i>
                            ) : (
                                <i className="fa-solid fa-chevron-down"></i>
                            )}
                        </div>
                    </div>
                    {isMenuOpen && (
                        <div
                            ref={menuRef}
                            className={clsx(`${baseClass}__fields__select--menu`, {
                                [`${baseClass}__fields__select--menu--top`]: menuPosition === 'top',
                                [`${baseClass}__fields__select--menu--bottom`]: menuPosition === 'bottom',
                            })}
                            tabIndex={-1}
                            onFocus={() => {
                                console.log('GET FOCUS')
                            }}
                            onBlur={() => {
                                console.log('LOST FOCUS')
                                // closeMenu()
                            }}
                        >
                            <div
                                onClick={(e) => {
                                    e.stopPropagation()
                                }}
                                className={clsx(`${baseClass}__fields__select--menu--search`, {
                                    [`${baseClass}__fields__select--menu--search--top`]: menuPosition === 'top',
                                    [`${baseClass}__fields__select--menu--search--bottom`]: menuPosition === 'bottom',
                                })}
                            >
                                <i className="fa-solid fa-magnifying-glass"></i>
                                <input
                                    ref={searchInputRef}
                                    id={`${groupId}--search-input`}
                                    className={`${baseClass}__fields__select--menu--search--input`}
                                    value={searchTerm}
                                    // onBlur={(e) => {
                                    //     closeMenu()
                                    // }}
                                    onChange={(e) => {
                                        setSearchTerm(e.target.value)
                                    }}
                                    placeholder={`${getTitle('8615', 'Search country')}...`}
                                />
                            </div>

                            <div className={`${baseClass}__fields__select--menu--list`} tabIndex={-1}>
                                {filteredCountries.map((item, index) => (
                                    <div
                                        key={`${item.prefix}--${item.name}`}
                                        ref={(el) => (itemRefs.current[index] = el)}
                                        // type="button"
                                        className={clsx(`${baseClass}__fields__select--menu--item`, {
                                            [`${baseClass}__fields__select--menu--item--selected`]:
                                                item.prefix === value?.prefix,
                                            [`${baseClass}__fields__select--menu--item--focused`]:
                                                index === focusedIndex,
                                        })}
                                        data-index={index}
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            handlePrefixSelect(item)
                                        }}
                                        onMouseEnter={() => setFocusedIndex(index)}
                                    >
                                        <div className={`${baseClass}__fields__select--menu--item--flag`}>
                                            <CountryIcon flag={item.flag} />
                                        </div>
                                        <div className={`${baseClass}__fields__select--menu--item--prefix`}>
                                            {item.prefix}
                                        </div>
                                        <div className={`${baseClass}__fields__select--menu--item--name`}>
                                            {item.name}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
                <ApplyV2InputText
                    ref={inputRef}
                    className={`${baseClass}__fields__input`}
                    name={`${name}.number`}
                    // label="Phone Number"
                    value={getDisplayValue()}
                    onChange={handleNumberChange}
                    // onInput={handleInput}
                    onBlur={(e) => {
                        if (onBlur) {
                            onBlur(e)
                        }
                        setIsInputFocused(false)
                    }}
                    onFocus={(e) => {
                        if (onFocus) {
                            onFocus(e)
                        }
                        setIsInputFocused(true)
                        closeMenu()
                    }}
                    placeholder={placeholder}
                    // errorMessage={getErrorMessage('number')}
                    errorMessage={errors}
                    isShowErrorMessage={false}
                    isLoading={isLoading}
                    // isRequired={isRequired}
                />
            </div>
            {description && (
                <div
                    className={clsx(`${baseClass}__description`, {
                        [`${className}__description`]: className,
                    })}
                >
                    {description}
                </div>
            )}
            {errorMessage && isShowErrorMessage && (
                <div className={`${baseClass}__error`}>
                    <div className={`${baseClass}__error--icon`}>
                        <WarningErrorIcon />
                    </div>
                    <div className={`${baseClass}__error--text`}>
                        {[...new Set([getErrorMessage('prefix'), getErrorMessage('number')])]
                            .filter(Boolean)
                            .join(', ')}
                    </div>
                </div>
            )}
        </div>
    )
}

export default ApplyV2InputPhone
